<template>
  <div class="min-h-[100dvh] bg-pitch-black flex flex-col font-inter text-total-white">
    <!-- Header -->

    <TopNavbar />
    <div
      v-if="!showDialpad && !showCallScreen"
      class="flex w-full items-center justify-between px-4 py-4 mb-4"
    >
      <a @click="goBack" class="text-prime-golden text-xl flex gap-2 items-center">
        <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back Button" />
      </a>
      <h1 class="text-2xl font-semibold mr-4">New Call</h1>
      <span></span>
    </div>

    <!-- Call Form -->
    <div v-if="!showDialpad && !showCallScreen" class="py-6 mb-12 flex-1">
      <!-- Project Field -->
      <div
        :class="[
          'flex relative items-center rounded gap-1 pl-4 pr-2 mb-4',
          projectDropdownOpen ? 'bg-dark-grey py-4' : 'bg-pitch-black py-2',
        ]"
      >
        <label
          :class="[
            'block text-total-white',
            projectDropdownOpen ? 'text-base' : 'text-sm',
          ]"
        >
          Project:
        </label>
        <div class="relative w-full bg-transparent px-2 flex items-center">
          <!-- When dropdown is closed, show selected project -->
          <div
            v-if="!projectDropdownOpen"
            class="w-full flex justify-between items-center cursor-pointer"
            @click="toggleProjectDropdown"
          >
            <span class="text-sm font-semibold text-total-white">{{
              selectedProject?.address_1
            }}</span>
            <i class="fas fa-chevron-down text-prime-golden"></i>
          </div>

          <!-- When dropdown is open, show search input -->
          <div v-else class="w-full flex items-center">
            <input
              v-model="projectSearchQuery"
              type="text"
              placeholder="Search projects..."
              class="w-full bg-transparent border-none text-total-white outline-none"
            />
            <button
              @click="closeProjectDropdown"
              class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Project Dropdown List -->
        <div
          v-if="projectDropdownOpen"
          class="absolute top-12 left-0 w-full text-left bg-pitch-black border border-dark-grey mt-2 z-10"
        >
          <ul>
            <li
              v-for="project in filteredProjects"
              :key="project"
              @click="selectProject(project)"
              class="p-4 bg-charcoal-grey border-b border-light-grey-translucent-dark hover:bg-dark-grey cursor-pointer"
            >
              {{ project?.address_1 }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Contact Field -->
      <div
        :class="[
          'flex relative items-center rounded  gap-1 pl-4 pr-2 mb-4',
          contactDropdownOpen ? 'bg-dark-grey py-4' : ' py-2',
        ]"
      >
        <label
          :class="['block text-total-white', contactDropdownOpen ? 'text-sm' : 'text-sm']"
        >
          Contact:
        </label>
        <div class="relative w-full bg-transparent px-2 flex items-center">
          <!-- When dropdown is closed, show selected contact -->
          <div
            v-if="!contactDropdownOpen"
            @click="toggleContactDropdown"
            class="w-full flex justify-between items-center cursor-pointer"
          >
            <span class="text-sm font-semibold text-total-white">{{
              selectedContact
                ? selectedContact.first_name + " " + selectedContact.last_name
                : ""
            }}</span>
            <i class="fas fa-chevron-down text-prime-golden"></i>
          </div>

          <!-- When dropdown is open, show search input -->
          <div v-else class="w-full flex items-center">
            <input
              v-model="contactSearchQuery"
              type="text"
              placeholder="Search contacts..."
              class="w-full bg-transparent border-none text-total-white outline-none"
            />
            <button
              @click="closeContactDropdown"
              class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Keypad Icon -->

        <!-- Contact Dropdown List -->
        <div
          v-if="contactDropdownOpen"
          class="absolute top-12 left-0 w-full text-left bg-eerie-black border border-dark-grey mt-2 z-10"
        >
          <ul>
            <li
              class="p-4 bg-pitch-black flex justify-between items-center border-b border-light-grey-translucent-dark hover:bg-dark-grey cursor-pointer"
            >
              <button
                @click="toggleDialpad"
                class="flex text-total-white-800 items-center gap-3"
              >
                <img
                  class="-mb-2"
                  src="../../assets/images/keypad.svg"
                  alt="keypad icon"
                />
                Keypad
              </button>
            </li>
            <li
              v-for="contact in filteredContacts"
              :key="contact.phone"
              @click="selectContact(contact)"
              class="p-4 bg-pitch-black flex justify-between items-center border-b border-light-grey-translucent-dark hover:bg-dark-grey cursor-pointer"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
                >
                  <template v-if="contact.profile_image_url">
                    <img
                      :src="contact.profile_image_url"
                      alt="Profile Image"
                      class="w-full h-full rounded-full"
                    />
                  </template>
                  <template v-else>
                    <i class="fa-solid fa-user text-2xl text-pitch-black"></i>
                  </template>
                </div>
                <span class="font-semibold">{{
                  contact.first_name + " " + contact.last_name
                }}</span>
              </div>
              <span class="text-sm">{{ contact.phone }}</span>
              <!-- Display phone number here -->
            </li>
          </ul>
        </div>
      </div>

      <!-- Call Action Button -->
    </div>
    <div
      v-if="!showDialpad && !showCallScreen && selectedContact && selectedProject"
      class="flex mb-16 items-center justify-center"
    >
      <button
        @click="startCall"
        class="bg-prime-golden flex justify-center items-center h-20 w-20 rounded-full"
      >
        <i class="fas fa-phone text-pitch-black text-3xl"></i>
      </button>
    </div>

    <div
      v-if="!showDialpad && !showCallScreen && (!selectedContact || !selectedProject)"
      class="flex mb-16 items-center justify-center opacity-40"
    >
      <button
        class="bg-prime-golden flex justify-center items-center h-20 w-20 rounded-full"
      >
        <i class="fas fa-phone text-pitch-black text-3xl"></i>
      </button>
    </div>

    <DialpadPhone
      v-if="showDialpad"
      @goBack="toggleDialpad"
      :selectedProject="selectedProject"
      @makeCall="startCall"
      @createContact="createContact"
      :isExistingContact="isExistingContact"
      :isDimmed="isCallInitiating"
    />
    <CallScreen
      v-if="showCallScreen"
      :selectedProject="selectedProject"
      :callerName="getCallerName()"
      :callerAvatar="selectedContact?.profile_image_url"
      :callState="callState"
      :callDuration="callDuration"
      @callStateChange="handleCallStateChange"
      @muteToggle="handleMuteToggle"
      @speakerToggle="handleSpeakerToggle"
    />
  </div>
</template>

<script>
import CallScreen from "@/components/CallScreen.vue";
import DialpadPhone from "@/components/DialpadPhone.vue";
import TopNavbar from "@/components/TopNavbar.vue";
import { Device } from "@twilio/voice-sdk"; // Import Twilio Device
import authService from "@/api/authService";
import { useContactsStore } from "@/stores/contact"; // Import contactsStore
import { useProjectStore } from "@/stores/project";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

export default {
  components: {
    DialpadPhone,
    CallScreen,
    TopNavbar,
  },

  setup() {
    const router = useRouter();
    const route = useRoute();
    const contactsStore = useContactsStore();
    const projectStore = useProjectStore();

    onMounted(async () => {
      await projectStore.fetchAllProjects();
      if (route.params.projectId || route.query.projectId) {
        await contactsStore.fetchContactsByProjectId(
          Number(route.params.projectId || route.query.projectId)
        );
      } else {
        await contactsStore.fetchAllContacts();
      }

      projects.value = projectStore.projects;
      contacts.value = contactsStore.contacts;

      if (route.params.contactId) {
        selectedContact.value = contactsStore.contacts.find(
          (contact) => contact.id == route.params.contactId
        );
      }
    });

    const selectedContact = ref(
      contactsStore?.contacts.find((contact) => contact.id == route.params.contactId) ||
        null
    ); // Default selected contact
    const contacts = ref(contactsStore?.contacts || []);
    const contactDropdownOpen = ref(false); // Toggle contact dropdown
    const contactSearchQuery = ref(""); // Search query for contacts
    const selectedProject = ref(
      projectStore?.projects.find((project) => project.id == route.params.projectId) ||
        null
    ); // Default selected project
    const callingNumber = ref(
      selectedProject.value?.project_manager_twilio_number?.phone_number || null
    );
    const projects = ref(projectStore?.projects || []);
    const projectDropdownOpen = ref(false); // Toggle project dropdown
    const projectSearchQuery = ref(""); // Search query for projects
    const showDialpad = ref(false);
    const callState = ref("incoming");
    const callDuration = ref("00:00");
    const showCallScreen = ref(false);
    const twilioDevice = ref(null); // Twilio Device instance
    const activeConnection = ref(null); // Active call connection
    const hangupCall = ref(() => {});
    const muteCall = ref(() => {});
    const toggleSpeaker = ref(() => {});
    const callStatus = ref("");
    const callDurationInterval = ref(null);
    const callStatusInterval = ref(null);
    const isCallInitiating = ref(false);
    const currentTab = ref(route.query.currentTab || "contacts");

    watch(selectedProject, async (newProj) => {
      if (!newProj) {
        await contactsStore.fetchAllContacts();
        contacts.value = contactsStore.contacts;
        callingNumber.value = null;
        selectedContact.value = null;
      } else if (route.params.projectId || route.query.projectId) {
        await projectStore.fetchProjectById(newProj.id);
        callingNumber.value =
          projectStore.currentProject?.project_manager_twilio_number?.phone_number ||
          null;
        await contactsStore.fetchContactsByProjectId(newProj.id);
        contacts.value = contactsStore.contacts;
        selectedContact.value = null;
      }
    });

    watch(selectedContact, (newContact) => {
      if (newContact) {
        console.log(newContact.phone);
      }
    });

    const isExistingContact = (phone) => {
      const existingContact = contacts.value.find((contact) => contact.phone === phone);
      return existingContact
        ? existingContact.first_name + " " + existingContact.last_name
        : "";
    };

    const createContact = (phone) => {
      router.push({
        name: "CreateContactPage",
        query: { phone: phone, projectId: selectedProject.value.id, from_new_call: true },
        params: { projectId: selectedProject.value.id },
      });
    };

    return {
      contacts,
      selectedContact,
      contactDropdownOpen,
      contactSearchQuery,
      selectedProject,
      projects,
      projectDropdownOpen,
      projectSearchQuery,
      showDialpad,
      callState,
      callDuration,
      showCallScreen,
      twilioDevice,
      activeConnection,
      hangupCall,
      callStatus,
      callDurationInterval,
      callStatusInterval,
      isCallInitiating,
      callingNumber,
      currentTab,
      isExistingContact,
      createContact,
    };
  },
  computed: {
    filteredContacts() {
      return this.contacts
        .filter(
          (contact) =>
            (contact.first_name
              .toLowerCase()
              .includes(this.contactSearchQuery.toLowerCase()) ||
              contact.phone
                .toLowerCase()
                .includes(this.contactSearchQuery.toLowerCase())) &&
            contact.phone &&
            contact.role !== "Project Manager" &&
            contact.role !== "Project Agent" // Exclude Project Agent
        )
        .map((contact) => {
          if (!contact.first_name && !contact.last_name) {
            contact.first_name = "Unknown";
            contact.last_name = "Unknown";
          }
          return contact;
        });
    },
    filteredProjects() {
      if (this.projectSearchQuery === "") {
        return this.projects;
      }
      return this.projects.filter((project) =>
        project?.address_1?.toLowerCase().includes(this.projectSearchQuery.toLowerCase())
      );
    },
  },

  methods: {
    toggleContactDropdown() {
      this.projectDropdownOpen = false;
      this.contactDropdownOpen = !this.contactDropdownOpen;
      this.contactSearchQuery = ""; // Reset search when opening
    },
    handleCallStateChange(newState) {
      this.callState = newState;
      if (newState === "endCall") {
        clearInterval(this.callDurationInterval);
        clearInterval(this.callStatusInterval);
        this.hangupCall();
        // Use the same redirection logic as goBack:
        if (this.$route.query.from_contact) {
          this.$router.push({
            name: "ContactViewPage",
            params: {
              id:
                this.$route.params.contactId ||
                (this.selectedContact && this.selectedContact.id),
            },
            query: { projectId: this.$route.query.projectId },
          });
        } else if (this.$route.query.currentTab) {
          this.$router.push({
            name: "ProjectPage",
            params: { id: this.$route.query.projectId || this.selectedProject?.id },
            query: { tab: this.$route.query.currentTab },
          });
        } else if (this.$route.query.projectId) {
          this.$router.push({
            name: "ProjectPage",
            params: { id: this.$route.query.projectId || this.selectedProject?.id },
            query: { tab: this.$route.query.currentTab || "contacts" },
          });
        } else {
          this.$router.go(-1);
        }
      }
    },
    getCallerName() {
      if (this.selectedContact?.first_name) {
        return this.selectedContact?.first_name + " " + this.selectedContact?.last_name;
      } else {
        return this.selectedContact?.phone;
      }
    },
    handleMuteToggle(isMuted) {
      this.muteCall(isMuted);
    },
    handleSpeakerToggle(isSpeakerOn) {
      this.toggleSpeaker(isSpeakerOn);
    },
    toggleDialpad() {
      this.showDialpad = !this.showDialpad;
      this.contactDropdownOpen = !this.contactDropdownOpen;
    },
    toggleCallScreen() {
      this.showCallScreen = !this.showCallScreen;
      this.contactDropdownOpen = !this.contactDropdownOpen;
    },
    selectContact(contact) {
      this.selectedContact = contact;
      this.contactDropdownOpen = false; // Close the dropdown after selection
      this.contactSearchQuery = ""; // Clear search query
    },
    closeContactDropdown() {
      this.contactDropdownOpen = false;
      this.contactSearchQuery = ""; // Clear search query when closing
    },
    toggleProjectDropdown() {
      this.contactDropdownOpen = false;
      this.projectDropdownOpen = !this.projectDropdownOpen;
      this.projectSearchQuery = ""; // Reset search when opening
    },
    selectProject(project) {
      this.selectedProject = project;
      this.projectDropdownOpen = false; // Close the dropdown after selection
      this.projectSearchQuery = ""; // Clear search query
    },
    closeProjectDropdown() {
      this.projectDropdownOpen = false;
      this.projectSearchQuery = ""; // Clear search query when closing
    },
    async startCall(number) {
      console.log(
        "Starting call with:",
        this.selectedProject,
        this.selectedContact,
        number
      );

      if (this.callingNumber == null) {
        console.log("No calling number found");
        return;
      } else {
        console.log("Calling number found:", this.callingNumber);
      }

      this.isCallInitiating = true;
      if (this.selectedContact == null && typeof number === "string") {
        const foundContact = this.contacts.find((contact) => contact.phone === number);
        if (foundContact) {
          this.selectedContact = foundContact;
        }
      }

      // Fetch the token from your backend
      const response = await authService.getTwilioToken();
      const { token } = response.data;

      // Initialize Twilio Device
      const device = new Device(token);

      device.register();

      // Handle device events
      device.on("registered", () => {
        console.log("Twilio Device is ready");
      });

      device.on("error", (error) => {
        console.error("Twilio Device error:", error);
      });

      device.on("connected", () => {
        console.log("Twilio Device connected");
      });

      device.on("connect", () => {
        console.log("Twilio Device connect");
      });

      device.on("disconnected", () => {
        console.log("Twilio Device disconnected");
      });

      device.on("ringing", () => {
        console.log("Twilio Device ringing");
      });

      const call = await device.connect({
        params: {
          To: typeof number === "string" ? number : this.selectedContact.phone,
          From: this.callingNumber,
        },
      });

      watch(this.callStatus, (callStatus) => {
        if (callStatus == "open") {
          this.isCallInitiating = false;
        }
      });

      this.twilioDevice = device;
      this.showCallScreen = true;
      this.showDialpad = false;
      this.callState = "active";
      this.callDuration = 0;
      this.callDurationInterval = setInterval(() => {
        if (this.callStatus == "open") {
          this.callDuration++;
        }
      }, 1000);
      this.callStatusInterval = setInterval(() => {
        this.callStatus = call.status();

        if (this.callStatus == "closed") {
          this.handleCallStateChange("endCall");
        }
      }, 1000);

      this.hangupCall = () => {
        device.destroy();
      };

      this.muteCall = (isMuted) => {
        call.mute(isMuted);
      };

      this.toggleSpeaker = async (isSpeakerOn) => {
        const audioDevices = await navigator.mediaDevices.enumerateDevices();
        console.log("Audio Devices:", audioDevices);

        const speaker = audioDevices.find(
          (device) => device.kind === "audiooutput" && device.label.includes("Speaker")
        );

        if (isSpeakerOn) {
          await device.audio.setVolume(0.2); // Set speaker volume to 20%
          console.log("Speaker volume set to 20%");
        } else {
          await device.audio.setVolume(1); // Set speaker volume to 100%
          console.log("Speaker volume set to 100%");
        }

        console.log("Speaker:", speaker);

        return;

        if (isSpeakerOn && speaker) {
          await device.audio.setSinkId(speaker.deviceId);
          console.log("Audio output set to speaker:", speaker.label);
        } else if (!isSpeakerOn) {
          await device.audio.setSinkId(null);
          console.log("Audio output set to default.");
        } else {
          console.warn("Speaker device not found. Using default audio output.");
        }
      };
    },
    goBack() {
      if (this.$route.query.from_contact) {
        // User started call from a contact page—return there.
        this.$router.push({
          name: "ContactViewPage",
          params: {
            id:
              this.$route.params.contactId ||
              (this.selectedContact && this.selectedContact.id),
          },
          query: { projectId: this.$route.query.projectId },
        });
      } else if (this.$route.query.currentTab) {
        this.$router.push({
          name: "ProjectPage",
          params: { id: this.$route.query.projectId || this.selectedProject?.id },
          query: { tab: this.$route.query.currentTab },
        });
      } else if (this.$route.query.projectId) {
        this.$router.push({
          name: "ProjectPage",
          params: { id: this.$route.query.projectId || this.selectedProject?.id },
          query: { tab: this.$route.query.currentTab || "contacts" },
        });
      } else {
        this.$router.go(-1);
      }
    },
    openKeypad() {
      console.log("Opening keypad...");
      // Logic to open the keypad
    },
  },
  beforeDestroy() {
    if (this.twilioDevice) {
      this.twilioDevice.destroy();
    }
  },
};
</script>

<style scoped></style>
